/**
 * The employee's own Rate API key, held for the length of an unlock.
 *
 * Their PIN is never stored — it is sent once to unlock, the server turns it into this
 * key, and the key is what every salary request carries from then on. The server keeps
 * no key of its own, so without this the salary pages have no way to price anything.
 *
 * The key is scoped by the Rate API to this employee's own rates: it reads their pay
 * rate and answers 404 for anybody else's. That is what makes it safe to hand to the
 * browser at all — unlike the admin key, which reads everyone.
 *
 * A second, separate slot from the admin key in `rateApi.service.ts`
 * (`salary-rate-passkey`): an admin is also an employee, so one browser can hold both,
 * and neither lock should ever open the other.
 *
 * The entry records WHOSE unlock it is, and every read is checked against the user
 * asking. Without that, one person unlocking and then logging out would leave the next
 * person to sign in on that machine already unlocked.
 *
 * Every access is guarded — a browser in private mode can throw on storage entirely,
 * and a hand-edited entry can be unparseable.
 */

const STORAGE_KEY = 'account-salary-unlock';

/** Same four hours the admin gate uses; see PASSKEY_TTL_MS in rateApi.service.ts. */
export const ACCOUNT_UNLOCK_TTL_MS = 4 * 60 * 60 * 1000;

interface StoredUnlock {
  key: string;
  expiresAt: number;
  userId: string;
}

function readStored(userId: string | undefined): StoredUnlock | null {
  if (!userId) return null;

  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: StoredUnlock;
  try {
    parsed = JSON.parse(raw) as StoredUnlock;
  } catch {
    clearAccountUnlock();
    return null;
  }

  if (!parsed?.key || typeof parsed.expiresAt !== 'number' || !parsed.userId) {
    clearAccountUnlock();
    return null;
  }
  if (Date.now() >= parsed.expiresAt) {
    clearAccountUnlock();
    return null;
  }
  // Someone else's unlock, left behind on a shared machine.
  if (parsed.userId !== userId) {
    clearAccountUnlock();
    return null;
  }
  return parsed;
}

export function setAccountUnlock(userId: string, key: string): void {
  const entry: StoredUnlock = {
    key,
    expiresAt: Date.now() + ACCOUNT_UNLOCK_TTL_MS,
    userId,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // The provider keeps its own in-memory copy for this page load.
  }
}

export function clearAccountUnlock(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export const hasAccountUnlock = (userId: string | undefined): boolean =>
  readStored(userId) !== null;

/** The employee's key for this user, or '' when they are not unlocked. */
export function getAccountKey(userId: string | undefined): string {
  return readStored(userId)?.key ?? '';
}

/**
 * The key without knowing who is asking — for the request interceptor, which runs
 * below the React tree and has no user in hand. Any entry that survives `readStored`'s
 * expiry check belongs to whoever last unlocked on this browser, and the server checks
 * the key against the caller's own token regardless.
 */
export function getAnyAccountKey(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as StoredUnlock;
    if (!parsed?.key || typeof parsed.expiresAt !== 'number') return '';
    return Date.now() < parsed.expiresAt ? parsed.key : '';
  } catch {
    return '';
  }
}

/** Epoch ms at which this user's unlock lapses, or null when not unlocked. */
export function accountUnlockExpiresAt(userId: string | undefined): number | null {
  return readStored(userId)?.expiresAt ?? null;
}
